import { useCallback, useEffect, useRef, useState } from 'react';

interface CourseVideoControlsProps {
  videoElement: HTMLVideoElement | null;
  onRecordClick?: () => void;
  showRecordButton?: boolean;
}

const MIN_RATE = 0.25;
const MAX_RATE = 1.5;
const RATE_STEP = 0.05;

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function CourseVideoControls({
  videoElement,
  onRecordClick,
  showRecordButton,
}: CourseVideoControlsProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const prevVolumeRef = useRef(1);

  // Sync state from video element events
  useEffect(() => {
    if (!videoElement) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => setCurrentTime(videoElement.currentTime);
    const onDurationChange = () => setDuration(videoElement.duration || 0);
    const onRateChange = () => setPlaybackRate(videoElement.playbackRate);
    const onVolumeChange = () => {
      setVolume(videoElement.volume);
      setIsMuted(videoElement.muted);
    };

    videoElement.addEventListener('play', onPlay);
    videoElement.addEventListener('pause', onPause);
    videoElement.addEventListener('timeupdate', onTimeUpdate);
    videoElement.addEventListener('durationchange', onDurationChange);
    videoElement.addEventListener('ratechange', onRateChange);
    videoElement.addEventListener('volumechange', onVolumeChange);

    // Init from current state
    setIsPlaying(!videoElement.paused);
    setCurrentTime(videoElement.currentTime);
    setDuration(videoElement.duration || 0);
    setPlaybackRate(videoElement.playbackRate);
    setVolume(videoElement.volume);
    setIsMuted(videoElement.muted);

    return () => {
      videoElement.removeEventListener('play', onPlay);
      videoElement.removeEventListener('pause', onPause);
      videoElement.removeEventListener('timeupdate', onTimeUpdate);
      videoElement.removeEventListener('durationchange', onDurationChange);
      videoElement.removeEventListener('ratechange', onRateChange);
      videoElement.removeEventListener('volumechange', onVolumeChange);
    };
  }, [videoElement]);

  const togglePlay = useCallback(() => {
    if (!videoElement) return;
    if (videoElement.paused) {
      videoElement.play();
    } else {
      videoElement.pause();
    }
  }, [videoElement]);

  const stop = useCallback(() => {
    if (!videoElement) return;
    videoElement.pause();
    videoElement.currentTime = 0;
  }, [videoElement]);

  const changeRate = useCallback((delta: number) => {
    if (!videoElement) return;
    const newRate = Math.round((playbackRate + delta) * 100) / 100;
    const clamped = Math.max(MIN_RATE, Math.min(MAX_RATE, newRate));
    videoElement.playbackRate = clamped;
  }, [videoElement, playbackRate]);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoElement || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    videoElement.currentTime = ratio * duration;
  }, [videoElement, duration]);

  const toggleMute = useCallback(() => {
    if (!videoElement) return;
    if (isMuted || volume === 0) {
      videoElement.muted = false;
      videoElement.volume = prevVolumeRef.current || 0.5;
    } else {
      prevVolumeRef.current = volume;
      videoElement.muted = true;
    }
  }, [videoElement, isMuted, volume]);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoElement) return;
    const val = parseFloat(e.target.value);
    videoElement.volume = val;
    videoElement.muted = val === 0;
  }, [videoElement]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === ' ' || e.key.toLowerCase() === 'p') {
        e.preventDefault();
        togglePlay();
      }
      if (e.key.toLowerCase() === 'o') {
        e.preventDefault();
        stop();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [togglePlay, stop]);

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const ratePercent = Math.round(playbackRate * 100);

  return (
    <div className="playback-bar" style={{ flexWrap: 'wrap' }}>
      {/* Playback controls */}
      <div className="playback-controls">
        <button
          className="btn btn-icon btn-icon-sm"
          onClick={togglePlay}
          title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
          style={{
            background: isPlaying ? 'var(--color-warning)' : 'var(--color-accent)',
            color: '#fff',
            border: 'none',
          }}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>
        {(isPlaying || currentTime > 0) && (
          <button
            className="btn btn-icon btn-icon-sm btn-ghost"
            onClick={stop}
            title="Stop (O)"
          >
            ⏹
          </button>
        )}
      </div>

      {/* Timeline */}
      <div style={{ flex: 1, minWidth: 120, display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
        <span className="playback-time" style={{ minWidth: 40 }}>{formatTime(currentTime)}</span>
        <div className="playback-timeline" onClick={handleSeek}>
          <div className="playback-timeline-progress" style={{ width: `${progressPercent}%` }} />
          <div className="playback-timeline-scrubber" style={{ left: `${progressPercent}%` }} />
        </div>
        <span className="playback-time" style={{ minWidth: 40 }}>{formatTime(duration)}</span>
      </div>

      {/* Speed control */}
      <div className="playback-tempo">
        <button
          className="btn btn-icon btn-icon-sm btn-ghost"
          onClick={() => changeRate(-RATE_STEP)}
          disabled={playbackRate <= MIN_RATE}
          title="Slower"
        >
          −
        </button>
        <span style={{
          minWidth: 48,
          textAlign: 'center',
          fontFamily: 'var(--font-mono)',
          fontSize: 13,
          fontWeight: playbackRate < 1 ? 700 : 400,
          color: playbackRate < 1 ? 'var(--color-accent)' : 'var(--color-text-secondary)',
        }}>
          {ratePercent}%
        </span>
        <button
          className="btn btn-icon btn-icon-sm btn-ghost"
          onClick={() => changeRate(RATE_STEP)}
          disabled={playbackRate >= MAX_RATE}
          title="Faster"
        >
          +
        </button>
      </div>

      {/* Volume */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
        <button
          className="btn btn-icon btn-icon-sm btn-ghost"
          onClick={toggleMute}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted || volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}
        </button>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={isMuted ? 0 : volume}
          onChange={handleVolumeChange}
          style={{ width: 60 }}
          title={`Volume: ${Math.round((isMuted ? 0 : volume) * 100)}%`}
        />
      </div>

      {/* Record button */}
      {showRecordButton && onRecordClick && (
        <button
          className="btn btn-sm"
          onClick={onRecordClick}
          style={{
            background: 'var(--color-incorrect)',
            color: '#fff',
            border: 'none',
          }}
        >
          ⏺ Record
        </button>
      )}
    </div>
  );
}
